import { useState, useEffect, useRef, useCallback } from 'react';

export interface UseDynamicTypingPlaceholderOptions {
  /** 占位文案列表，会按顺序循环展示 */
  placeholders: string[];
  /** 每打一个字的间隔（毫秒） */
  typingSpeed?: number;
  /** 打完后停留多久再开始删（毫秒） */
  pauseBeforeDelete?: number;
  /** 每删一个字的间隔（毫秒） */
  deleteSpeed?: number;
  /** 删完后停留多久再打下一个（毫秒） */
  pauseBeforeNext?: number;
}

export interface UseDynamicTypingPlaceholderResult {
  /** 当前占位文案，用户打过字后为空且不再更新 */
  displayText: string;
  /** 在输入框 onChange 里调用一次，之后动画永久停止 */
  markUserHasTyped: () => void;
}

type Phase = 'typing' | 'pausing' | 'deleting' | 'waitingNext';

/**
 * 给定占位文案列表，像打字机一样：逐字打出 → 停留 → 逐字删除 → 短暂停顿 → 打下一条，循环。
 * 调用 markUserHasTyped() 后（例如在 input onChange 中）动画停止且不再恢复。
 */
export function useDynamicTypingPlaceholder({
  placeholders,
  typingSpeed = 80,
  pauseBeforeDelete = 1500,
  deleteSpeed = 50,
  pauseBeforeNext = 300,
}: UseDynamicTypingPlaceholderOptions): UseDynamicTypingPlaceholderResult {
  const [displayText, setDisplayText] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  /** 用户曾在输入框里打过字 → 之后不再显示打字占位动画 */
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const enabled = !hasUserTyped;
  const markUserHasTyped = useCallback(() => setHasUserTyped(true), []);
  const phaseRef = useRef<Phase>('typing');
  const charIndexRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const nextTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const placeholdersRef = useRef(placeholders);
  placeholdersRef.current = placeholders;

  useEffect(() => {
    return () => {
      if (nextTimeoutRef.current) clearTimeout(nextTimeoutRef.current);
    };
  }, []);

  // enabled 为 false 时停掉循环并清空占位；只依赖 placeholderIndex，避免 placeholders 新引用导致 effect 反复跑
  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
      if (nextTimeoutRef.current) clearTimeout(nextTimeoutRef.current);
      nextTimeoutRef.current = undefined;
      setDisplayText('');
      return () => {};
    }

    const list = placeholdersRef.current;
    if (list.length === 0) return;

    const currentText = list[placeholderIndex];

    const run = () => {
      const phase = phaseRef.current;
      if (phase === 'typing') {
        if (charIndexRef.current < currentText.length) {
          setDisplayText(currentText.slice(0, charIndexRef.current + 1));
          charIndexRef.current += 1;
          timeoutRef.current = setTimeout(run, typingSpeed);
        } else {
          phaseRef.current = 'pausing';
          timeoutRef.current = setTimeout(run, pauseBeforeDelete);
        }
      } else if (phase === 'pausing') {
        phaseRef.current = 'deleting';
        timeoutRef.current = setTimeout(run, 0);
      } else if (phase === 'deleting') {
        if (charIndexRef.current > 0) {
          charIndexRef.current -= 1;
          setDisplayText(currentText.slice(0, charIndexRef.current));
          timeoutRef.current = setTimeout(run, deleteSpeed);
        } else {
          phaseRef.current = 'waitingNext';
          nextTimeoutRef.current = setTimeout(() => {
            setPlaceholderIndex((i) => (i + 1) % placeholdersRef.current.length);
          }, pauseBeforeNext);
        }
      }
    };

    phaseRef.current = 'typing';
    charIndexRef.current = 0;
    run();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
      if (nextTimeoutRef.current) clearTimeout(nextTimeoutRef.current);
      nextTimeoutRef.current = undefined;
    };
  }, [enabled, placeholderIndex]);

  return { displayText, markUserHasTyped };
}
